/**
 * GDI Styles Utility
 *
 * Resolves the GDI StylesPerViewType pattern into Chakra UI responsive props.
 *
 * Input: Array of style objects from Optimizely, each with a ViewTypesToApply field
 * that contains comma-separated breakpoints (e.g., "Web - Mobile,Web - Tablet")
 *
 * Output: Object with Chakra responsive values (base/md/lg keys)
 */

export const GDI_VIEW_TYPE_MAP = {
    'App': null, // Not used on web
    'Web - Mobile': 'base',
    'Web - Tablet': 'md',
    'Web - Desktop': 'lg',
    'Web - Desktop Hover': '_hover',
    'Web - Desktop - Hover': '_hover',
    // CMS also sends without spaces/dashes
    'WebMobile': 'base',
    'WebTablet': 'md',
    'WebDesktop': 'lg',
    'WebDesktopHover': '_hover'
}

/**
 * Standard field map for GDITextBlockStylesPerViewType → Chakra UI CSS props.
 * Use this with resolveGDIStyles() wherever a GDITextBlock's style array is consumed.
 * Note: FontStyle maps directly to fontStyle (e.g. 'italic'). Components that need
 * richer font parsing (bold, bold-italic, etc.) should use their own map + FONT_STYLE_MAP.
 */
export const GDI_TEXT_BLOCK_FIELD_MAP = {
    TextAlignment: 'textAlign',
    TextColor: 'color',
    TextShadow: 'textShadow',
    BackgroundColor: 'backgroundColor',
    FontStyle: 'fontStyle',
    FontSize: 'fontSize',
    LineHeight: 'lineHeight',
    TextMaxWidth: 'maxWidth',
    Paddings: 'padding',
    Margins: 'margin'
}

const HOVER_KEY = '_hover'
export const GDI_DESKTOP_MEDIA_QUERY = '@media screen and (min-width: 62em)'
const GDI_RESPONSIVE_MEDIA_QUERY = {
    md: '@media screen and (min-width: 48em)',
    lg: GDI_DESKTOP_MEDIA_QUERY
}

/**
 * Parse ViewTypesToApply string into an array of Chakra breakpoint keys
 * @param {string} viewTypes - e.g., "Web - Mobile,Web - Tablet"
 * @returns {string[]} - e.g., ['base', 'md'] or ['_hover']
 */
export const parseGDIViewTypes = (viewTypes) => {
    if (!viewTypes) return []
    return viewTypes
        .split(',')
        .map((v) => GDI_VIEW_TYPE_MAP[v.trim()])
        .filter(Boolean)
}

const ALL_BREAKPOINTS = ['base', 'md', 'lg']

export const hasGDIViewTypes = (entry) => Boolean(entry?.ViewTypesToApply?.trim?.())

export const fillGDIResponsiveInheritance = (values) => {
    if (!values || typeof values !== 'object' || Array.isArray(values)) return values

    const out = {}
    let inheritedValue
    let hasInheritedValue = false

    ALL_BREAKPOINTS.forEach((bp) => {
        if (values[bp] !== undefined) {
            inheritedValue = values[bp]
            hasInheritedValue = true
            out[bp] = values[bp]
        } else if (hasInheritedValue) {
            out[bp] = inheritedValue
        }
    })

    return out
}

export const getGDIDesktopHoverSx = (hoverStyles) =>
    Object.keys(hoverStyles || {}).length > 0
        ? {[GDI_DESKTOP_MEDIA_QUERY]: {'&:hover': hoverStyles}}
        : undefined

const isPlainObject = (value) =>
    value && typeof value === 'object' && !Array.isArray(value)

export const splitGDIResponsiveStyles = (styles) => {
    const directStyles = {}
    const sxStyles = {}

    Object.entries(styles || {}).forEach(([prop, value]) => {
        if (!isPlainObject(value)) {
            directStyles[prop] = value
            return
        }

        if (value.base !== undefined) directStyles[prop] = value.base

        Object.entries(GDI_RESPONSIVE_MEDIA_QUERY).forEach(([bp, mediaQuery]) => {
            if (value[bp] === undefined) return
            sxStyles[mediaQuery] = {
                ...(sxStyles[mediaQuery] || {}),
                [prop]: value[bp]
            }
        })
    })

    return {
        directStyles,
        sxStyles: Object.keys(sxStyles).length ? sxStyles : undefined
    }
}

export const getGDINestedResponsiveSx = (selector, styles) => {
    const {directStyles, sxStyles} = splitGDIResponsiveStyles(styles)
    const sx = {}

    if (Object.keys(directStyles).length > 0) {
        sx[selector] = directStyles
    }

    Object.entries(sxStyles || {}).forEach(([mediaQuery, mediaStyles]) => {
        sx[mediaQuery] = {[selector]: mediaStyles}
    })

    return Object.keys(sx).length ? sx : undefined
}

export const mergeGDISx = (...sxObjects) => {
    const merged = {}

    sxObjects.filter(Boolean).forEach((sx) => {
        Object.entries(sx).forEach(([key, value]) => {
            merged[key] =
                isPlainObject(merged[key]) && isPlainObject(value)
                    ? {...merged[key], ...value}
                    : value
        })
    })

    return Object.keys(merged).length ? merged : undefined
}

const normalizeStyleValue = (v) => (typeof v === 'string' ? v.trim() : v)

const hasValue = (v) => {
    if (v === undefined || v === null) return false
    if (typeof v === 'string') {
        const trimmed = v.trim()
        return trimmed !== '' && trimmed !== 'Default / Inherit'
    }
    return true
}

/* Detect a design-system typography token (e.g. "bodyXsSRegular", "cta", "headingMd").
 * Tokens are camelCase identifiers — start with a lowercase letter, then letters/digits only.
 * Anything else (e.g. "16px", "1.2rem", "100%", "12 14") is treated as a literal CSS value. */
const TOKEN_RE = /^[a-z][a-zA-Z0-9]*$/
const isTextStyleToken = (v) => typeof v === 'string' && TOKEN_RE.test(v)

/**
 * Apply a single style entry's fields to the result at the given breakpoints.
 *
 * Special-case: when the chakra prop is `fontSize` and the authored value matches a
 * design-system token (e.g. `bodyXsSRegular`), the value is routed to Chakra's
 * `textStyle` prop instead so the entire token (font-size, line-height, weight, color)
 * is applied. Literal CSS values (`16px`, `1rem`, `100%`) keep going to `fontSize`.
 */
const applyStyleEntry = (result, styleEntry, breakpoints, fieldMap) => {
    for (const [optiField, chakraProp] of Object.entries(fieldMap)) {
        const rawValue = styleEntry[optiField]
        if (!hasValue(rawValue)) continue
        const value = normalizeStyleValue(rawValue)

        const targetProp =
            chakraProp === 'fontSize' && isTextStyleToken(value) ? 'textStyle' : chakraProp

        if (!result[targetProp]) result[targetProp] = {}
        for (const bp of breakpoints) {
            result[targetProp][bp] = value
        }
    }
}

/**
 * Resolve an array of GDI StylesPerViewType objects into Chakra responsive props.
 *
 * Entries without ViewTypesToApply are defaults: their values apply to ALL web
 * breakpoints. Entries with ViewTypesToApply set their declared breakpoints,
 * then inherit upward in mobile-first order.
 *
 * "Web - Desktop Hover" / "WebDesktopHover" entries are returned separately so the
 * component can apply them through a desktop-only hover selector.
 *
 * Example:
 *   Input:  [{ ViewTypesToApply: "WebMobile", Color: "black", BorderColor: "black" },
 *            { ViewTypesToApply: "WebDesktopHover", Color: "white", BorderColor: "red" }]
 *   Output: { styles: { color: { base: 'black', md: 'black', lg: 'black' },
 *                        borderColor: { base: 'black', md: 'black', lg: 'black' } },
 *             hoverStyles: { color: 'white', borderColor: 'red' } }
 *
 * @param {Array} stylesPerViewType - Array from Optimizely
 * @param {Object} fieldMap - Maps Optimizely field names to Chakra prop names
 * @returns {{ styles: Object, hoverStyles: Object }} Chakra responsive props + hover props
 */
export const resolveGDIStyles = (stylesPerViewType, fieldMap) => {
    if (!Array.isArray(stylesPerViewType) || stylesPerViewType.length === 0 || !fieldMap) {
        return {styles: {}, hoverStyles: {}}
    }

    const result = {}
    const hoverResult = {}

    const defaults = []
    const targeted = []

    stylesPerViewType.forEach((entry) => {
        const hasAuthoredViewTypes = hasGDIViewTypes(entry)
        const breakpoints = parseGDIViewTypes(entry?.ViewTypesToApply)

        if (!hasAuthoredViewTypes) {
            defaults.push(entry)
        } else if (breakpoints.length > 0) {
            targeted.push({entry, breakpoints})
        }
    })

    defaults.forEach((entry) => {
        applyStyleEntry(result, entry, ALL_BREAKPOINTS, fieldMap)
    })

    targeted.forEach(({entry, breakpoints}) => {
        const regularBreakpoints = breakpoints.filter((bp) => bp !== HOVER_KEY)
        const hasHover = breakpoints.includes(HOVER_KEY)

        if (regularBreakpoints.length > 0) {
            applyStyleEntry(result, entry, regularBreakpoints, fieldMap)
        }

        if (hasHover) {
            // Hover styles are flat (not responsive) and are wrapped at the component.
            // FontSize tokens are routed to `textStyle` (same rule as the non-hover branch).
            for (const [optiField, chakraProp] of Object.entries(fieldMap)) {
                const rawValue = entry[optiField]
                if (!hasValue(rawValue)) continue
                const value = normalizeStyleValue(rawValue)
                const targetProp =
                    chakraProp === 'fontSize' && isTextStyleToken(value) ? 'textStyle' : chakraProp
                hoverResult[targetProp] = value
            }
        }
    })

    Object.keys(result).forEach((prop) => {
        result[prop] = fillGDIResponsiveInheritance(result[prop])
    })

    return {styles: result, hoverStyles: hoverResult}
}

/**
 * Resolve a GDI HideBlockOn string into Chakra display props.
 * Compatible with the legacy getHideStyles but uses the GDI field naming.
 *
 * Accepts both CMS token spellings: "Web - Mobile" / "WebMobile" (with or
 * without the space-dash prefix). The CMS is inconsistent across blocks.
 *
 * @param {string} hideBlockOn - Comma-separated breakpoints, e.g.,
 *   "Web - Mobile,Web - Desktop" or "WebMobile,WebDesktop"
 * @returns {Object} - { display: { base: 'none', md: 'block', lg: 'none' }, doNotLoadComponent: false }
 */
export const getGDIHideStyles = (hideBlockOn) => {
    if (!hideBlockOn) return {}

    const devices = new Set(hideBlockOn.split(',').map((d) => d.trim()))
    const display = {base: 'block', md: 'block', lg: 'block'}
    let allHidden = true

    const hides = (...tokens) => tokens.some((t) => devices.has(t))

    if (hides('Web - Mobile', 'WebMobile')) {
        display.base = 'none'
    } else {
        allHidden = false
    }

    if (hides('Web - Tablet', 'WebTablet')) {
        display.md = 'none'
    } else {
        allHidden = false
    }

    if (hides('Web - Desktop', 'WebDesktop')) {
        display.lg = 'none'
    } else {
        allHidden = false
    }

    if (allHidden) {
        return {doNotLoadComponent: true}
    }

    /* Mirror `display` into raw media-query sx so consumers that compose
     * authored responsive styles via `splitGDIResponsiveStyles` /
     * `mergeGDISx` can fold the hide rules into the same cascade layer.
     * The Chakra responsive `display={...}` prop emits styles in a layer
     * that loses to raw `@media` rules already in `sx`, so without this
     * the hide is silently overridden on hidden breakpoints. */
    const sx = {
        display: display.base === 'none' ? 'none' : 'block',
        [GDI_RESPONSIVE_MEDIA_QUERY.md]: {
            display: display.md === 'none' ? 'none' : 'block'
        },
        [GDI_RESPONSIVE_MEDIA_QUERY.lg]: {
            display: display.lg === 'none' ? 'none' : 'block'
        }
    }

    return {display, sx}
}

/* ------------------------------------------------------------------ */
/* Shared CMS-style mappings and helpers                              */
/* ------------------------------------------------------------------ */

/**
 * Field map for the `GDITextBlockStylesPerViewType` /
 * `GDIRewardTierTableBlockStylesPerViewType` CMS shape.
 *
 * Translates the CMS field names to Chakra prop names so consumers can pass
 * it straight into `resolveGDIStyles(stylesPerViewType, GDI_TEXT_BLOCK_STYLE_MAP)`.
 * `FontStyle` is intentionally NOT part of this map — it is resolved separately
 * via `resolveFontStyleProps` because a single string can map to either
 * `fontWeight` or `fontStyle` (e.g. "bold italic").
 */
export const GDI_TEXT_BLOCK_STYLE_MAP = {
    TextAlignment: 'textAlign',
    TextColor: 'color',
    BackgroundColor: 'backgroundColor',
    FontSize: 'fontSize',
    LineHeight: 'lineHeight',
    TextShadow: 'textShadow',
    TextMaxWidth: 'maxWidth',
    Paddings: 'padding',
    Margins: 'margin'
}

/**
 * Maps the authored `FontStyle` string token to the Chakra typography props
 * that actually express it. A single token can produce multiple props
 * (`bold italic` → fontWeight + fontStyle), or fall back to a `textStyle`
 * design-system token when the value is a camelCase identifier.
 */
export const FONT_STYLE_MAP = {
    bold: {fontWeight: 'bold'},
    medium: {fontWeight: 'medium'},
    light: {fontWeight: '300'},
    roman: {fontWeight: 'normal', fontStyle: 'normal'},
    normal: {fontWeight: 'normal', fontStyle: 'normal'},
    italic: {fontStyle: 'italic'},
    oblique: {fontStyle: 'oblique'},
    'bold-italic': {fontWeight: 'bold', fontStyle: 'italic'},
    'bold italic': {fontWeight: 'bold', fontStyle: 'italic'},
    underline: {textDecoration: 'underline'},
    strikethrough: {textDecoration: 'line-through'},
    'line-through': {textDecoration: 'line-through'},
    uppercase: {textTransform: 'uppercase'},
    lowercase: {textTransform: 'lowercase'},
    capitalize: {textTransform: 'capitalize'}
}

/**
 * Resolve a single `FontStyle` value (e.g. "italic", "bold italic",
 * "bodySmRegular") into Chakra props. Falls back to `{textStyle: token}`
 * when the value is a camelCase identifier (design-system token).
 *
 * @param {string} value
 * @returns {Object} Chakra props (subset of fontWeight / fontStyle /
 *   textDecoration / textTransform / textStyle)
 */
export const resolveFontStyleProps = (value) => {
    if (!value) return {}
    const trimmed = String(value).trim()
    const mapped = FONT_STYLE_MAP[trimmed.toLowerCase()]
    if (mapped) return mapped
    // Design-system textStyle tokens are camelCase identifiers (e.g. "largeTitle2",
    // "bodyXsSRegular"). If the value doesn't map to a CSS font modifier, treat
    // it as a textStyle token so the full typography scale is applied.
    if (/^[a-z][a-zA-Z0-9]*$/.test(trimmed)) return {textStyle: trimmed}
    return {}
}

/**
 * Resolve a `FontStyle` value that may be either a flat string or a
 * responsive `{base, md, lg}` object. For responsive input, every
 * resolved Chakra prop is itself promoted to a responsive object so the
 * breakpoint scoping is preserved.
 */
export const resolveResponsiveFontStyleProps = (value) => {
    const resolved = {}

    if (value && typeof value === 'object' && !Array.isArray(value)) {
        Object.entries(value).forEach(([breakpoint, breakpointValue]) => {
            Object.entries(resolveFontStyleProps(breakpointValue)).forEach(([prop, propValue]) => {
                if (!resolved[prop]) resolved[prop] = {}
                resolved[prop][breakpoint] = propValue
            })
        })
        return resolved
    }

    return resolveFontStyleProps(value)
}

/**
 * Merge `source` into `target` in place, deep-merging plain-object values
 * so responsive `{base, md, lg}` shapes from different resolution passes
 * accumulate instead of overwriting each other.
 */
export const mergeResolvedStyles = (target, source) => {
    Object.entries(source).forEach(([prop, value]) => {
        if (
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            target[prop] &&
            typeof target[prop] === 'object' &&
            !Array.isArray(target[prop])
        ) {
            target[prop] = {...target[prop], ...value}
            return
        }

        target[prop] = value
    })
}

/**
 * Resolve a CMS `*StylesPerViewType` array (e.g.
 * `GDITextBlockStylesPerViewType`, `GDIRewardTierTableBlockStylesPerViewType`)
 * into the props needed to render a single Chakra text node.
 *
 * Pipeline:
 *   1. `resolveGDIStyles` with `GDI_TEXT_BLOCK_STYLE_MAP` for the standard
 *      text/layout fields (TextColor, BackgroundColor, FontSize, Paddings,
 *      Margins, LineHeight, TextShadow, TextMaxWidth, TextAlignment).
 *   2. A second `resolveGDIStyles` pass for `FontStyle`, fed through
 *      `resolveFontStyleProps` so values like "italic", "bold italic" or a
 *      design-system token expand to the correct Chakra props.
 *   3. `splitGDIResponsiveStyles` to separate `base` values (spread as direct
 *      props) from breakpoint values (folded into raw `@media` sx).
 *   4. Layer the theme `fallback` under the direct props so the CMS only
 *      needs to author the bits the author actually wants to override.
 *   5. Merge any caller-supplied `extraSx` (e.g. an `<img>` reset selector)
 *      into the responsive sx via `mergeGDISx`.
 *
 * @param {Array} stylesPerViewType - Raw CMS array.
 * @param {Object} [fallback={}] - Theme-default props layered under CMS values.
 * @param {Object} [extraSx] - Additional sx rules to merge with the resolved sx.
 * @returns {{ directStyles: Object, sxStyles: Object | undefined }}
 */
export const resolveCmsTextStyles = (stylesPerViewType, fallback = {}, extraSx) => {
    const {styles: responsive} = resolveGDIStyles(stylesPerViewType, GDI_TEXT_BLOCK_STYLE_MAP)
    const {styles: fontStyleResponsive} = resolveGDIStyles(stylesPerViewType, {
        FontStyle: 'fontStyle'
    })

    const fontStyleValue = fontStyleResponsive.fontStyle
    if (typeof fontStyleValue === 'string') {
        Object.assign(responsive, resolveFontStyleProps(fontStyleValue))
    } else if (fontStyleValue && typeof fontStyleValue === 'object') {
        Object.entries(fontStyleValue).forEach(([bp, raw]) => {
            const resolved = resolveFontStyleProps(raw)
            Object.entries(resolved).forEach(([prop, val]) => {
                if (!responsive[prop] || typeof responsive[prop] !== 'object') {
                    responsive[prop] = {}
                }
                responsive[prop][bp] = val
            })
        })
    }

    const {directStyles, sxStyles} = splitGDIResponsiveStyles(responsive)
    return {
        directStyles: {...fallback, ...directStyles},
        sxStyles: mergeGDISx(sxStyles, extraSx)
    }
}
